import { useState } from "react";
import { Link } from "react-router-dom";
import { Users, FileQuestion, MessageSquare, Pencil, Loader2, ExternalLink } from "lucide-react";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useUpdateSite } from "@/hooks/use-sites";
import { useAuth } from "@/auth/useAuth";
import type { Site } from "@/api/types";

export function SiteInspector({ data }: { data: unknown }) {
  const site = data as Site;
  const updateSite = useUpdateSite();
  const { can } = useAuth();
  const canEdit = can("edc_data:write");

  const [editing, setEditing] = useState(false);
  const [editStatus, setEditStatus] = useState(site.status);
  const [editPI, setEditPI] = useState(site.principalInvestigator ?? "");
  const [editCity, setEditCity] = useState(site.city ?? "");
  const [editCountry, setEditCountry] = useState(site.country ?? "");

  return (
    <div className="space-y-6">
      <Link
        to={`/studies/${site.studyId}/sites/${site.id}`}
        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
      >
        <ExternalLink className="h-3 w-3" /> View Full Details
      </Link>
      <div>
        <h3 className="text-lg font-semibold text-foreground">{site.name}</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">Site #{site.siteNumber}</p>
        <div className="mt-2">
          <StatusBadge status={site.status} />
        </div>
      </div>

      {!editing && (
        <div className="space-y-2">
          {site.principalInvestigator && (
            <Detail label="Principal Investigator" value={site.principalInvestigator} />
          )}
          {site.country && (
            <Detail label="Location" value={[site.city, site.country].filter(Boolean).join(", ")} />
          )}
          <Detail label="EDC System" value={site.edcSystem} />
        </div>
      )}

      {canEdit && !editing && (
        <button
          onClick={() => setEditing(true)}
          data-testid="site-edit-btn"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80"
        >
          <Pencil className="h-3 w-3" /> Edit Details
        </button>
      )}

      {editing && (
        <div className="space-y-3 border-t border-border pt-4" data-testid="site-edit-form">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Status</label>
            <select
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            >
              {["active", "inactive", "closed"].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Principal Investigator</label>
            <input type="text" value={editPI} onChange={(e) => setEditPI(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground" />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">City</label>
            <input type="text" value={editCity} onChange={(e) => setEditCity(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground" />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Country</label>
            <input type="text" value={editCountry} onChange={(e) => setEditCountry(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground" />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                updateSite.mutate(
                  {
                    siteId: site.id,
                    status: editStatus,
                    principalInvestigator: editPI || undefined,
                    city: editCity || undefined,
                    country: editCountry || undefined,
                  },
                  { onSuccess: () => setEditing(false) },
                );
              }}
              disabled={updateSite.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {updateSite.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setEditStatus(site.status);
                setEditPI(site.principalInvestigator ?? "");
                setEditCity(site.city ?? "");
                setEditCountry(site.country ?? "");
              }}
              className="rounded-md border border-border px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-border p-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-4 w-4" /><span className="text-xs">Subjects</span>
          </div>
          <p className="mt-1 text-xl font-semibold text-foreground">{site.subjectCount ?? 0}</p>
        </div>
        <div className="rounded-lg border border-border p-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <FileQuestion className="h-4 w-4" /><span className="text-xs">Queries</span>
          </div>
          <p className="mt-1 text-xl font-semibold text-foreground">{site.queryCount ?? 0}</p>
        </div>
      </div>

      <div className="space-y-2">
        <Link
          to={`/subjects?studyId=${site.studyId}&siteId=${site.edcSiteId}`}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          <Users className="h-4 w-4" /> View Subjects
        </Link>
        <Link
          to={`/queries?studyId=${site.studyId}&siteId=${site.edcSiteId}`}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          <FileQuestion className="h-4 w-4" /> View Queries
        </Link>
        <Link
          to="/chat"
          className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <MessageSquare className="h-4 w-4" /> Ask AI about this site
        </Link>
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  );
}
